import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CustomTableMenuItem,
  FilterDefinition,
  SelectionState,
  styled,
} from "@monolith-forensics/monolith-ui";

import {
  GetStorageQuery,
  StorageBulkActionItem,
  StorageItem,
  StorageTable,
  StorageTableHandle,
  storageQueryKeys,
  useCaseContext,
  useLegacyStoragePopupAdapter,
} from "@/Features";
import { usePermissions } from "@/hooks/usePermissions";
import type { useTable } from "@/Monolith-UI/Table/Table.js";
import { SharedCaseTabListQuery } from "@/pages/Cases/types/Cases";
import AssignStoragePopup from "@/pages/Storage/modals/AssignStoragePopup.js";
import { DestroyItemsPopup } from "@/pages/Storage/modals/DestroyItemsPopup.js";
import { MoveItemsPopup } from "@/pages/Storage/modals/MoveItemsPopup.js";
import { ReleaseItemsPopup } from "@/pages/Storage/modals/ReleaseItemsPopup.js";
import { RemoveItemsPopup } from "@/pages/Storage/modals/RemoveItemsPopup";
import { HardDriveUploadIcon } from "lucide-react";

/**
 * `:v2` intentionally breaks from the legacy `case:storage:view` key, which
 * holds the legacy table's column state in a shape the monolith-ui Table
 * cannot read.
 */
export const CASE_STORAGE_TABLE_STATE_KEY = "cases:storage:view:v2";

type CaseStoragePopup = "move" | "release" | "remove" | "destroy" | "assign";

const BULK_ACTIONS: Array<{ value: CaseStoragePopup; label: string }> = [
  { value: "move", label: "Move Selected Items" },
  { value: "release", label: "Release Selected Items" },
  { value: "remove", label: "Remove Selected Items" },
  { value: "destroy", label: "Destroy Selected Items" },
];

// Stable fallbacks for the first render, before StorageTable sets its handle.
const NO_RECORDS: StorageItem[] = [];
const NO_FILTER_DEFINITIONS: FilterDefinition[] = [];

const StyledPage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

export const CaseStoragePage = () => {
  const caseContext = useCaseContext();
  const caseId: number | undefined = caseContext?.data?.case_id;

  const queryClient = useQueryClient();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const canUpdate = hasPermission(MONOLITH_PERMISSIONS.STORAGE_UPDATE);

  const tableRef = useRef<StorageTableHandle>();
  const [openPopup, setOpenPopup] = useState<CaseStoragePopup | null>(null);
  const [selectionState, setSelectionState] = useState<SelectionState>();

  // Backstop kept for parity with CaseEvidencePage. StorageTable already
  // drops a saved selection before it mounts and clears the selection when
  // the case changes, so this only matters if the table is already loaded.
  useLayoutEffect(() => {
    tableRef.current?.clearSelections?.();
  }, []);

  const clearSelections = useCallback(() => {
    tableRef.current?.clearSelections();
  }, []);

  // The handle is read during render. Every path that opens a popup changes
  // page state (a selection change, then `setOpenPopup`), so the popup always
  // mounts with the rows and query from the table's latest render.
  const legacy = useLegacyStoragePopupAdapter({
    selectionState,
    records: tableRef.current?.getRecords() ?? NO_RECORDS,
    resolvedQuery:
      tableRef.current?.getResolvedQuery() ??
      ({ case_id: caseId } as GetStorageQuery),
    filterDefinitions:
      tableRef.current?.getFilterDefinitions() ?? NO_FILTER_DEFINITIONS,
    clearSelections,
  });

  const closePopup = useCallback(() => setOpenPopup(null), []);

  // Same as the legacy page's handleModalAction. Bulk move, release, remove
  // and destroy write storage_coc rows; the Current Location tooltips cache
  // CoC per item (disabled until hovered), so invalidate (not refetch) to mark
  // them stale for the next hover.
  const handlePopupSubmit = useCallback(() => {
    tableRef.current?.refresh();
    queryClient.invalidateQueries({
      queryKey: storageQueryKeys.cocRecordsAll,
    });
  }, [queryClient]);

  // RemoveItemsPopup, unlike Destroy, does not clear the selection itself.
  // The removed rows would stay selected (mfui keeps the keys across the
  // refetch and the adapter caches selected rows), so a following bulk action
  // would act on items that are no longer in this case.
  const handleRemoveSubmit = useCallback(() => {
    clearSelections();
    handlePopupSubmit();
  }, [clearSelections, handlePopupSubmit]);

  const bulkActionItems: StorageBulkActionItem[] = BULK_ACTIONS.map(
    ({ value, label }) => ({
      value,
      label,
      visible: canUpdate,
      onClick: () => setOpenPopup(value),
    })
  );

  const toolbarItems: CustomTableMenuItem[] = canUpdate
    ? [
        {
          type: "button",
          position: "right",
          options: {
            children: <HardDriveUploadIcon size={14} />,
            size: "xxs",
            variant: "outlined",
            style: { padding: "0px 4px" },
            title: "Assign Storage",
            onClick: () => setOpenPopup("assign"),
          },
        },
      ]
    : [];

  if (!caseContext) {
    throw new Error("Case context not found");
  }
  if (!caseId) return null;

  const caseInfo = { case_id: caseId };

  return (
    <StyledPage>
      <StorageTable
        tableRef={tableRef}
        query={{ case_id: caseId }}
        stateStoreKey={CASE_STORAGE_TABLE_STATE_KEY}
        showSelection
        bulkActionItems={bulkActionItems}
        toolbarItems={toolbarItems}
        onSelectionChange={setSelectionState}
      />
      <MoveItemsPopup
        onSubmit={handlePopupSubmit}
        caseInfo={caseInfo}
        open={openPopup === "move"}
        handleClose={closePopup}
        table={legacy.table}
        query={legacy.query}
      />
      <ReleaseItemsPopup
        onSubmit={handlePopupSubmit}
        caseInfo={caseInfo}
        open={openPopup === "release"}
        handleClose={closePopup}
        table={legacy.table}
        query={legacy.query}
      />
      {/* RemoveItemsPopup types `table` as the full legacy useTable() return
          and `query` as SharedCaseTabListQuery; the adapter's runtime shapes
          match what it reads (see useLegacyStoragePopupAdapter). */}
      <RemoveItemsPopup
        onSubmit={handleRemoveSubmit}
        caseInfo={caseInfo}
        open={openPopup === "remove"}
        handleClose={closePopup}
        table={legacy.table as unknown as ReturnType<typeof useTable>}
        query={legacy.query as unknown as SharedCaseTabListQuery}
      />
      <AssignStoragePopup
        onSubmit={handlePopupSubmit}
        caseInfo={caseInfo}
        open={openPopup === "assign"}
        handleClose={closePopup}
        table={legacy.table}
      />
      <DestroyItemsPopup
        onSubmit={handlePopupSubmit}
        caseInfo={caseInfo}
        open={openPopup === "destroy"}
        handleClose={closePopup}
        table={legacy.table}
        query={legacy.query}
      />
    </StyledPage>
  );
};
